/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import IconButton from 'fe-design-base/molecules/IconButton';

import { getCurrentLocationName, getWebview } from 'selectors/session';

import {
  fetchProductTaskManager as getFetchProductTaskManager,
  fetchTaskManagerSubscriptions as getFetchTaskManagerSubscriptions,
} from 'features/managePlan/actions';
import WebViewAlt from 'features/managePlan/ManagePlanView/WebViewAlt';
import { selectProductTaskManagerPrice } from 'features/managePlan/selectors';

import Drawer from 'components/Drawer';

import { cxHelpers } from 'util/className';
import { toI18n } from 'util/i18n';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
  TRACK_ACTION_TYPES,
} from 'util/tracking_constants';
import { useTrackUx, withUxRoot } from 'util/uxEvents';

import { selectInitialViewKey } from './selectors';
import { closeTaskManagerCheckoutDrawer as getCloseTaskManagerCheckoutDrawer } from './slice';
import * as views from './views';

const { cx } = cxHelpers('TaskManagerCheckoutDrawer');

export const TaskManagerCheckoutDrawer = () => {
  const subscriptionType = 'task_manager';
  const dispatch = useDispatch();
  const trackUxEvent = useTrackUx();

  const initialViewKey = useSelector(selectInitialViewKey);
  const currentLocationName = useSelector(getCurrentLocationName);
  const price = useSelector(selectProductTaskManagerPrice);
  const isWebView = useSelector(getWebview);

  const [currentViewKey, setCurrentViewKey] = useState(initialViewKey);
  const [isLoading, setIsLoading] = useState(false);
  const CurrentView = views[currentViewKey as keyof typeof views];

  // Get Purchase Abandoned event data for Task Manager checkout
  const getPurchaseAbandonedEventData = useCallback(
    (closingWindow: boolean) => ({
      selected_plan: null, // No plan selection in add-on only checkout
      selected_billing_frequency: null, // No billing frequency for add-ons
      selected_add_on: ['task_manager'],
      promo_code: null, // No promo code support in current implementation
      closing_window: closingWindow,
    }),
    []
  );

  const handleTracking = useCallback(() => {
    if (currentViewKey === 'Success') {
      trackUxEvent(EVENT_ACTIONS.BUTTON_CLICKED, TRACK_ACTION_TYPES.CLICK, {
        element: 'close_button',
        stage: 'checkout_completed',
      });
    } else {
      trackUxEvent(
        'Purchase Abandoned',
        TRACK_ACTION_TYPES.CLICK,
        getPurchaseAbandonedEventData(false)
      );
    }
  }, [currentViewKey, trackUxEvent, getPurchaseAbandonedEventData]);

  const closeTaskManagerCheckoutDrawer = useCallback(() => {
    handleTracking();

    dispatch(getCloseTaskManagerCheckoutDrawer());
  }, [dispatch, handleTracking]);

  const fetchProductTaskManager = useCallback(() => {
    dispatch(getFetchProductTaskManager());
  }, [dispatch]);

  const fetchTaskManagerSubscriptions = useCallback(() => {
    dispatch(getFetchTaskManagerSubscriptions());
  }, [dispatch]);

  const handleOnLoading = useCallback((loading: boolean) => {
    setIsLoading(loading);
  }, []);

  const handleOnSuccess = useCallback(() => {
    setCurrentViewKey('Success');
    fetchTaskManagerSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // fetchLocationTaskPolicies();
    fetchProductTaskManager();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add beforeunload event listener to detect window/tab closing
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Track Purchase Abandoned event when closing window/tab
      handleTracking();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [trackUxEvent, handleTracking]);

  if (isWebView) {
    return (
      <Drawer
        className={cx()}
        maxWidth="464px"
        open
        variant="temporary"
        onClose={closeTaskManagerCheckoutDrawer}
      >
        <Box hright mt={16} mr={16}>
          <IconButton
            onClick={closeTaskManagerCheckoutDrawer}
            icon="Close"
            size="small"
            uxElement="checkout_drawer.close_button"
            label={toI18n('checkout_drawer.close_button_labels.task_manager')}
          />
        </Box>
        <WebViewAlt />
      </Drawer>
    );
  }

  return (
    <Drawer
      className={cx()}
      maxWidth="464px"
      open
      variant="temporary"
      onClose={closeTaskManagerCheckoutDrawer}
    >
      <Box hright mt={16} mr={16}>
        <IconButton
          onClick={closeTaskManagerCheckoutDrawer}
          icon="Close"
          size="small"
          uxElement="checkout_drawer.close_button"
          label={toI18n('checkout_drawer.close_button_labels.task_manager')}
        />
      </Box>
      <Box mt={12} ph={32}>
        <CurrentView
          onSuccess={handleOnSuccess}
          onLoading={handleOnLoading}
          isLoading={isLoading}
          closeDrawer={closeTaskManagerCheckoutDrawer}
          locationName={currentLocationName}
          price={price}
          subscriptionType={subscriptionType}
        />
      </Box>
    </Drawer>
  );
};
export default withUxRoot({
  productArea: PRODUCT_AREAS.TASK_MANAGER,
  eventCategory: EVENT_CATEGORIES.CHECKOUT,
})(TaskManagerCheckoutDrawer);
